// S13's pure arithmetic — the scrap Pareto (Module 05 §4 S13).
//
// THE QUESTION: "why did we scrap last month, ranked by what it cost, and what
// do I fix first". A Pareto answers the second half with a CUMULATIVE share:
// the two causes that together account for 80% of the money are the two worth a
// meeting. That number is only honest if three things hold, and all three are
// this file's job:
//
//  1. EXACT ARITHMETIC. Money is integer minor units and quantity is exact
//     decimal text (D-041). Both fold in `bigint` — a percentage computed
//     through a float is a percentage that can print 100.00000000000001% at the
//     bottom of a column a factory director is reading.
//
//  2. NEVER SUM WHAT DOES NOT SUM (§9.14). `total_qty` never crosses a UoM and
//     money never crosses a currency. So a Pareto is not one list: it is one
//     list PER RANKING UNIT. With money visible that is one list per currency
//     (a facility has one functional currency, so in practice one list); with
//     money redacted it is one list per UoM, because "300 m and 4 000 kg" has
//     no sum and pretending otherwise would rank a cause by the accident of
//     which unit its material happens to be measured in.
//
//  3. THE RANKING IS HONEST ABOUT WHAT IT RANKS BY. §7 redacts the ledger's
//     cost from qc / planner / storekeeper / floor / maintenance BY ABSENCE.
//     The old shape of this problem — render the money column empty and keep
//     the money ORDER — is the worst of both: the screen would claim a ranking
//     it cannot show the basis for, computed from numbers the caller was
//     refused. Instead the basis SWITCHES to quantity and the screen says so.
//     `basis` is what it says with.
//
// Shares are BASIS POINTS (0–10 000), integers, never floats. The cumulative
// column is computed from the RUNNING RAW TOTAL rather than by adding rounded
// shares, so it lands on exactly 100.00% at the last row instead of 99.97%.

import type { WasteRollupRow } from "../hooks/waste-queries";

/** What one Pareto list ranks BY — and therefore what its percentages mean. */
export type ParetoBasis =
  | { readonly kind: "money"; readonly currency: string }
  | { readonly kind: "qty"; readonly uom: string };

export interface ParetoRow {
  /** The cause (or category) id; `null` is the unattributed bucket — real
   * scrap the plant recorded without a cause, which is a finding, not a gap. */
  readonly key: string | null;
  readonly eventCount: number;
  /** The ranking magnitude, exact: minor units for money, thousandths for qty. */
  readonly value: bigint;
  /** Share of this list's total, in basis points. `null` when the total is
   * zero — "0% of nothing" is a claim, and the screen must say so instead. */
  readonly shareBp: number | null;
  /** Running share through this row inclusive, basis points; `null` as above. */
  readonly cumulativeBp: number | null;
  /** Quantity lines for this row, per UoM, exact decimal text (§9.14). Carried
   * on money lists too — a GM ranking by tenge still wants the metres. */
  readonly qtyByUom: readonly { uom: string; qty: string }[];
}

export interface ParetoList {
  readonly basis: ParetoBasis;
  readonly total: bigint;
  readonly rows: readonly ParetoRow[];
}

/** numeric(_,3) decimal text → exact thousandths. */
export function milliOf(text: string): bigint {
  const negative = text.startsWith("-");
  const bare = negative ? text.slice(1) : text;
  const [whole = "0", frac = ""] = bare.split(".");
  const milli = BigInt(whole || "0") * 1000n + BigInt((frac + "000").slice(0, 3));
  return negative ? -milli : milli;
}

/** Exact thousandths → the decimal text the rest of the app formats. */
export function textOfMilli(milli: bigint): string {
  const negative = milli < 0n;
  const abs = negative ? -milli : milli;
  return `${negative ? "-" : ""}${String(abs / 1000n)}.${String(abs % 1000n).padStart(3, "0")}`;
}

/**
 * Is the money visible on this whole answer?
 *
 * ALL-OR-NOTHING, on purpose, and it matches `shiftColumnsOf`'s rule one screen
 * over: §7 redacts per caller, not per row, so a mixed set means something has
 * gone wrong upstream — and a Pareto assembled from "the buckets that happened
 * to carry cost" would rank causes by how much of their money survived a
 * filter. Absent on any row ⇒ the whole board falls back to quantity.
 */
export function moneyVisible(rows: readonly WasteRollupRow[]): boolean {
  return rows.length > 0 && rows.every((row) => row.total_cost_minor !== undefined);
}

interface Accumulator {
  value: bigint;
  eventCount: number;
  qtyByUom: Map<string, bigint>;
}

function emptyAcc(): Accumulator {
  return { value: 0n, eventCount: 0, qtyByUom: new Map() };
}

/**
 * Fold rollup buckets into ranked Pareto lists.
 *
 * `keyOf` picks the dimension (cause for S13's default, category for its
 * month-end-by-type view) so one implementation serves both — the ranking maths
 * does not care which id it is ranking, and two copies of it would be two
 * places for the cumulative column to drift.
 */
export function paretoOf(
  rows: readonly WasteRollupRow[],
  keyOf: (row: WasteRollupRow) => string | null,
): ParetoList[] {
  if (rows.length === 0) return [];
  const money = moneyVisible(rows);

  // One bucket-set per ranking unit: currency when ranking money, UoM when
  // ranking quantity. This is where §9.14 is enforced structurally rather than
  // remembered.
  const lists = new Map<string, Map<string | null, Accumulator>>();
  for (const row of rows) {
    const unit = money ? row.currency : row.uom;
    const byKey = lists.get(unit) ?? new Map<string | null, Accumulator>();
    const key = keyOf(row);
    const acc = byKey.get(key) ?? emptyAcc();
    acc.value += money ? BigInt(row.total_cost_minor ?? 0) : milliOf(row.total_qty);
    acc.eventCount += row.event_count;
    acc.qtyByUom.set(row.uom, (acc.qtyByUom.get(row.uom) ?? 0n) + milliOf(row.total_qty));
    byKey.set(key, acc);
    lists.set(unit, byKey);
  }

  const out: ParetoList[] = [];
  for (const [unit, byKey] of lists) {
    const entries = [...byKey.entries()];
    // Biggest first; ties broken deterministically so two renders of one answer
    // never disagree about the order (and so the tests can assert it at all).
    // The unattributed bucket sorts LAST among equals — it is a real row, but a
    // named cause is the more actionable of two identical numbers.
    entries.sort((a, b) => {
      if (a[1].value !== b[1].value) return a[1].value > b[1].value ? -1 : 1;
      if (a[0] === b[0]) return 0;
      if (a[0] === null) return 1;
      if (b[0] === null) return -1;
      return a[0] < b[0] ? -1 : 1;
    });
    const total = entries.reduce((sum, [, acc]) => sum + acc.value, 0n);
    let running = 0n;
    const paretoRows: ParetoRow[] = entries.map(([key, acc]) => {
      running += acc.value;
      return {
        key,
        eventCount: acc.eventCount,
        value: acc.value,
        shareBp: total > 0n ? bp(acc.value, total) : null,
        // From the RUNNING RAW total, not from summed rounded shares.
        cumulativeBp: total > 0n ? bp(running, total) : null,
        qtyByUom: [...acc.qtyByUom.entries()].map(([uom, milli]) => ({
          uom,
          qty: textOfMilli(milli),
        })),
      };
    });
    out.push({
      basis: money ? { kind: "money", currency: unit } : { kind: "qty", uom: unit },
      total,
      rows: paretoRows,
    });
  }

  // Money: by currency, so the order is stable across renders (one in practice).
  // Quantity: biggest stream first — with no money to rank by, the unit that
  // holds the most is the one a GM should read first.
  out.sort((a, b) => {
    if (a.basis.kind === "money" && b.basis.kind === "money") {
      return a.basis.currency < b.basis.currency ? -1 : 1;
    }
    if (a.total !== b.total) return a.total > b.total ? -1 : 1;
    return unitOf(a) < unitOf(b) ? -1 : 1;
  });
  return out;
}

function unitOf(list: ParetoList): string {
  return list.basis.kind === "money" ? list.basis.currency : list.basis.uom;
}

/** `value / total` in basis points, half-up, exact integer arithmetic. */
function bp(value: bigint, total: bigint): number {
  return Number((value * 20_000n + total) / (total * 2n));
}

/** Basis points → the "12.34" a `%`-suffixed cell prints. */
export function formatBp(basisPoints: number): string {
  return (basisPoints / 100).toFixed(2);
}

/**
 * How many rows carry the given cumulative share — "the 3 causes behind 80% of
 * the money". The Pareto's actual advice, computed rather than eyeballed.
 * `null` when the shares themselves are unknowable (a zero total).
 */
export function rowsToReach(list: ParetoList, thresholdBp: number): number | null {
  if (list.total <= 0n) return null;
  let n = 0;
  for (const row of list.rows) {
    n += 1;
    if (row.cumulativeBp !== null && row.cumulativeBp >= thresholdBp) return n;
  }
  return list.rows.length;
}
